import { useRef, useState, useEffect, useCallback } from "react";
import {
  ImageSegmenter,
  FilesetResolver,
  ImageSegmenterResult,
} from "@mediapipe/tasks-vision";

const MEDIAPIPE_CDN =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm";
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter_landscape/float16/latest/selfie_segmenter_landscape.tflite";

const DESKTOP_FPS = 30;
const IOS_FPS = 15;
const DESKTOP_SCALE = 0.67;
const IOS_SCALE = 0.5;

function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  return (
    /iP(hone|ad|od)/.test(ua) ||
    (/Safari/.test(ua) && /Apple Computer/.test(navigator.vendor) && /Mobile/.test(ua))
  );
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Timeout after ${ms}ms: ${label}`)), ms)
    ),
  ]);
}

async function validateCaptureStream(
  canvas: HTMLCanvasElement
): Promise<boolean> {
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;

  ctx.fillStyle = "red";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const testStream = canvas.captureStream(1);
  const [track] = testStream.getVideoTracks();
  if (!track || track.readyState !== "live") {
    testStream.getTracks().forEach((t) => t.stop());
    return false;
  }

  const testVideo = document.createElement("video");
  testVideo.muted = true;
  testVideo.playsInline = true;
  testVideo.srcObject = testStream;
  testVideo.style.display = "none";
  document.body.appendChild(testVideo);

  try {
    await testVideo.play();
    await new Promise<void>((resolve) => {
      testVideo.addEventListener("timeupdate", () => resolve(), { once: true });
      setTimeout(resolve, 1000);
    });

    const checkCanvas = document.createElement("canvas");
    checkCanvas.width = 4;
    checkCanvas.height = 4;
    const checkCtx = checkCanvas.getContext("2d");
    if (!checkCtx) return false;
    checkCtx.drawImage(testVideo, 0, 0, 4, 4);
    const pixels = checkCtx.getImageData(0, 0, 4, 4).data;
    const allZero = pixels.every((v) => v === 0);
    return !allZero;
  } finally {
    testStream.getTracks().forEach((t) => t.stop());
    testVideo.srcObject = null;
    testVideo.remove();
  }
}

export interface UseBackgroundBlurReturn {
  isBlurEnabled: boolean;
  isLoading: boolean;
  isSupported: boolean | null;
  toggleBlur: () => void;
  blurredStream: MediaStream | null;
}

export function useBackgroundBlur(
  stream: MediaStream | undefined,
  replaceVideoTrack: (
    oldTrack: MediaStreamTrack,
    newTrack: MediaStreamTrack
  ) => void
): UseBackgroundBlurReturn {
  const [isBlurEnabled, setIsBlurEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSupported, setIsSupported] = useState<boolean | null>(null);
  const [blurredStream, setBlurredStream] = useState<MediaStream | null>(null);

  const segmenterRef = useRef<ImageSegmenter | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const hiddenVideoRef = useRef<HTMLVideoElement | null>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const personCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const outputCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const captureTrackRef = useRef<MediaStreamTrack | null>(null);
  const activeTrackRef = useRef<MediaStreamTrack | null>(null);
  const processingRef = useRef(false);
  const lastFrameTimeRef = useRef(0);
  const enableIdRef = useRef(0);
  const isIos = useRef(isIosSafari());

  const cleanup = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    if (segmenterRef.current) {
      segmenterRef.current.close();
      segmenterRef.current = null;
    }

    if (captureTrackRef.current) {
      captureTrackRef.current.stop();
      captureTrackRef.current = null;
    }

    if (hiddenVideoRef.current) {
      hiddenVideoRef.current.srcObject = null;
      hiddenVideoRef.current.remove();
      hiddenVideoRef.current = null;
    }

    maskCanvasRef.current = null;
    personCanvasRef.current = null;
    outputCanvasRef.current = null;
    processingRef.current = false;
    lastFrameTimeRef.current = 0;
  }, []);

  const enableBlur = useCallback(async () => {
    if (!stream) return;

    const enableId = ++enableIdRef.current;
    setIsLoading(true);

    const ios = isIos.current;
    const fps = ios ? IOS_FPS : DESKTOP_FPS;
    const scale = ios ? IOS_SCALE : DESKTOP_SCALE;
    const delegate = ios ? "CPU" : "GPU";
    const targetMs = 1000 / fps;

    try {
      const [vision] = await withTimeout(
        Promise.all([
          FilesetResolver.forVisionTasks(MEDIAPIPE_CDN),
        ]),
        10000,
        "FilesetResolver"
      );

      if (enableIdRef.current !== enableId) return;

      const segmenter = await withTimeout(
        ImageSegmenter.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: MODEL_URL,
            delegate,
          },
          runningMode: "VIDEO",
          outputCategoryMask: false,
          outputConfidenceMasks: true,
        }),
        10000,
        "ImageSegmenter.createFromOptions"
      );

      if (enableIdRef.current !== enableId) {
        segmenter.close();
        return;
      }

      segmenterRef.current = segmenter;

      // Hidden video element - feeds the segmenter
      const hiddenVideo = document.createElement("video");
      hiddenVideo.muted = true;
      hiddenVideo.playsInline = true;
      hiddenVideo.srcObject = stream;
      hiddenVideo.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none;z-index:-1;";
      document.body.appendChild(hiddenVideo);
      hiddenVideoRef.current = hiddenVideo;
      await hiddenVideo.play();

      // Wait until the browser has decoded the first frame so videoWidth/videoHeight are non-zero.
      if (hiddenVideo.videoWidth === 0) {
        await new Promise<void>((resolve) => {
          hiddenVideo.addEventListener("loadedmetadata", () => resolve(), { once: true });
          // Hard cap: if metadata never fires (e.g. stream already loaded), resolve after 2s.
          setTimeout(resolve, 2000);
        });
      }

      if (enableIdRef.current !== enableId) {
        cleanup();
        return;
      }

      // Derive canvas dimensions from the actual video feed, scaled down for performance.
      // Falls back to 640×360 if videoWidth is still 0 (e.g. audio-only edge case).
      const sourceWidth = hiddenVideo.videoWidth || 640;
      const sourceHeight = hiddenVideo.videoHeight || 360;
      const width = Math.max(1, Math.round(sourceWidth * scale));
      const height = Math.max(1, Math.round(sourceHeight * scale));

      // Input canvas: video drawn at target size. Passed to the segmenter so
      // the mask output matches our canvas dimensions exactly (avoids row-offset
      // banding from a resolution mismatch).
      const inputCanvas = document.createElement("canvas");
      inputCanvas.width = width;
      inputCanvas.height = height;
      const inputCtx = inputCanvas.getContext("2d")!;

      // Create canvases
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = width;
      maskCanvas.height = height;
      const maskCtx = maskCanvas.getContext("2d", { willReadFrequently: true })!;
      maskCanvasRef.current = maskCanvas;

      const personCanvas = document.createElement("canvas");
      personCanvas.width = width;
      personCanvas.height = height;
      personCanvasRef.current = personCanvas;

      const outputCanvas = document.createElement("canvas");
      outputCanvas.width = width;
      outputCanvas.height = height;
      outputCanvasRef.current = outputCanvas;

      // Three-step downscale for heavy background blur.
      // video → 1/4 → 1/16 → 1/64 total, each step uses bilinear interpolation.
      const blur1 = document.createElement("canvas");
      blur1.width = Math.max(2, Math.round(width / 2));
      blur1.height = Math.max(2, Math.round(height /2));
      const blur1Ctx = blur1.getContext("2d")!;
      blur1Ctx.imageSmoothingEnabled = true;
      blur1Ctx.imageSmoothingQuality = "high";

      const blur2 = document.createElement("canvas");
      blur2.width = Math.max(2, Math.round(blur1.width / 3));
      blur2.height = Math.max(2, Math.round(blur1.height / 3));
      const blur2Ctx = blur2.getContext("2d")!;
      blur2Ctx.imageSmoothingEnabled = true;
      blur2Ctx.imageSmoothingQuality = "high";

      // Validate captureStream before committing
      const supported = await validateCaptureStream(outputCanvas);
      if (enableIdRef.current !== enableId) {
        cleanup();
        return;
      }
      if (!supported) {
        cleanup();
        setIsSupported(false);
        setIsLoading(false);
        return;
      }

      // Create the output stream from the canvas
      const captureStream = ios
        ? outputCanvas.captureStream(fps)
        : outputCanvas.captureStream(0);
      const [captureTrack] = captureStream.getVideoTracks();
      captureTrackRef.current = captureTrack;

      // Build the blurred stream: canvas video track + original audio tracks
      const fullBlurredStream = new MediaStream([
        captureTrack,
        ...stream.getAudioTracks(),
      ]);

      // Track the original video track for replaceTrack
      const originalTrack = stream.getVideoTracks()[0];
      if (originalTrack) {
        activeTrackRef.current = originalTrack;
        replaceVideoTrack(originalTrack, captureTrack);
        activeTrackRef.current = captureTrack;
      }

      setBlurredStream(fullBlurredStream);
      setIsSupported(true);
      setIsLoading(false);
      setIsBlurEnabled(true);

      // Start the RAF loop
      const personCtx = personCanvas.getContext("2d")!;
      const outputCtx = outputCanvas.getContext("2d")!;

      const processFrame = (now: number) => {
        rafIdRef.current = requestAnimationFrame(processFrame);

        if (now - lastFrameTimeRef.current < targetMs) return;
        if (processingRef.current) return;
        if (!hiddenVideoRef.current || hiddenVideoRef.current.readyState < 2) return;

        processingRef.current = true;
        lastFrameTimeRef.current = now;

        const seg = segmenterRef.current;
        if (!seg) {
          processingRef.current = false;
          return;
        }

        // Draw video at target resolution — the segmenter will output a mask
        // at this same size, keeping all canvases in the same coordinate space.
        inputCtx.drawImage(hiddenVideoRef.current, 0, 0, width, height);

        seg.segmentForVideo(
          inputCanvas,
          now,
          (result: ImageSegmenterResult) => {
            try {
              // selfie_segmenter_landscape: confidenceMasks[0] = person confidence
              // (1.0 = person, 0.0 = background). Use directly as person alpha.
              const confidenceMask = result.confidenceMasks?.[0];
              if (!confidenceMask) return;

              const maskData = confidenceMask.getAsFloat32Array();
              const imageData = new ImageData(width, height);
              for (let i = 0; i < maskData.length; i++) {
                imageData.data[i * 4 + 3] = Math.round(maskData[i] * 255);
              }

              // Temp A (mask canvas): person alpha mask
              maskCtx.putImageData(imageData, 0, 0);

              // Temp B (person canvas): sharp video clipped to person shape.
              personCtx.clearRect(0, 0, width, height);
              personCtx.globalCompositeOperation = "source-over";
              personCtx.drawImage(inputCanvas, 0, 0);
              personCtx.globalCompositeOperation = "destination-in";
              personCtx.drawImage(maskCanvas, 0, 0);

              // Two-step downscale-upscale blur: input → 1/4 → 1/16 → output.
              blur1Ctx.drawImage(inputCanvas, 0, 0, blur1.width, blur1.height);
              blur2Ctx.drawImage(blur1, 0, 0, blur2.width, blur2.height);

              outputCtx.clearRect(0, 0, width, height);
              outputCtx.imageSmoothingEnabled = true;
              outputCtx.imageSmoothingQuality = "high";
              outputCtx.drawImage(blur2, 0, 0, width, height);
              outputCtx.drawImage(personCanvas, 0, 0);

              // On desktop, manually request a frame for precise sync
              if (!ios && captureTrackRef.current) {
                (captureTrackRef.current as any).requestFrame?.();
              }
            } finally {
              processingRef.current = false;
            }
          }
        );
      };

      rafIdRef.current = requestAnimationFrame(processFrame);
    } catch (err) {
      if (enableIdRef.current === enableId) {
        cleanup();
        setIsSupported(false);
        setIsLoading(false);
      }
    }
  }, [stream, replaceVideoTrack, cleanup]);

  const disableBlur = useCallback(() => {
    const currentCaptureTrack = captureTrackRef.current;
    const originalTrack = stream?.getVideoTracks()[0];

    cleanup();

    if (originalTrack && currentCaptureTrack) {
      replaceVideoTrack(currentCaptureTrack, originalTrack);
      activeTrackRef.current = originalTrack;
    }

    setBlurredStream(null);
    setIsBlurEnabled(false);
  }, [stream, replaceVideoTrack, cleanup]);

  const toggleBlur = useCallback(() => {
    if (isLoading) return;
    if (isBlurEnabled) {
      disableBlur();
    } else {
      enableBlur();
    }
  }, [isLoading, isBlurEnabled, enableBlur, disableBlur]);

  // Cleanup when stream changes or component unmounts
  useEffect(() => {
    if (!stream && isBlurEnabled) {
      cleanup();
      setIsBlurEnabled(false);
      setBlurredStream(null);
    }
  }, [stream, isBlurEnabled, cleanup]);

  useEffect(() => {
    return () => {
      enableIdRef.current++;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isBlurEnabled, isLoading, isSupported, toggleBlur, blurredStream };
}
